import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { LabComboMapping, AnalysisCombo } from '../../../types/analysisCombo';
import { labComboMappingService } from '../../../services/labComboMappingService';
import { analysisComboService } from '../../../services/analysisComboService';
import { SageItemAutocomplete } from '../../shared/SageItemAutocomplete';
import { useSnackbar } from 'notistack';

interface LabComboMappingsEditorProps {
  labId: number;
  disabled?: boolean;
}

interface MappingRow extends LabComboMapping {
  comboName?: string;
}

const LabComboMappingsEditor: React.FC<LabComboMappingsEditorProps> = ({ labId, disabled = false }) => {
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [combos, setCombos] = useState<AnalysisCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newComboId, setNewComboId] = useState<number | ''>('');
  const [newSageItemId, setNewSageItemId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingsData, combosData] = await Promise.all([
        labComboMappingService.getMappings(labId),
        analysisComboService.getCombos(),
      ]);

      const activeCombos = combosData.filter((c) => c.isActive);
      setCombos(activeCombos);

      const enriched: MappingRow[] = mappingsData.map((m) => ({
        ...m,
        comboName: combosData.find((c) => c.id === m.analysisComboId)?.name,
      }));
      setMappings(enriched);
    } catch {
      enqueueSnackbar('Failed to load combo mappings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [labId, enqueueSnackbar]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial data fetch on mount; setLoading is part of the async fetch lifecycle
    loadData();
  }, [loadData]);

  const handleToggleActive = async (mapping: MappingRow) => {
    setSaving(true);
    try {
      await labComboMappingService.updateMapping(labId, mapping.id, {
        sageItemId: mapping.sageItemId,
        isActive: !mapping.isActive,
      });
      setMappings((prev) =>
        prev.map((m) => (m.id === mapping.id ? { ...m, isActive: !m.isActive } : m)),
      );
    } catch {
      enqueueSnackbar('Failed to update mapping', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSageItemChange = async (mapping: MappingRow, sageItemId: number | null) => {
    if (sageItemId === null) return;
    setSaving(true);
    try {
      await labComboMappingService.updateMapping(labId, mapping.id, {
        sageItemId,
        isActive: mapping.isActive,
      });
      setMappings((prev) =>
        prev.map((m) => (m.id === mapping.id ? { ...m, sageItemId } : m)),
      );
      enqueueSnackbar('Sage item updated', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update Sage item', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMapping = async () => {
    if (!newComboId || !newSageItemId) return;

    setSaving(true);
    try {
      const created = await labComboMappingService.createMapping(labId, {
        analysisComboId: newComboId as number,
        sageItemId: newSageItemId,
      });
      const combo = combos.find((c) => c.id === newComboId);
      setMappings((prev) => [...prev, { ...created, comboName: combo?.name }]);
      setAddingNew(false);
      setNewComboId('');
      setNewSageItemId(null);
      enqueueSnackbar('Combo mapping added', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to add combo mapping', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mapping: MappingRow) => {
    setSaving(true);
    try {
      await labComboMappingService.deleteMapping(labId, mapping.id);
      setMappings((prev) => prev.filter((m) => m.id !== mapping.id));
      enqueueSnackbar('Combo mapping deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete combo mapping', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const usedComboIds = mappings.map((m) => m.analysisComboId);
  const availableCombos = combos.filter((c) => !usedComboIds.includes(c.id));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Combo Mappings
        </Typography>
        {!addingNew && availableCombos.length > 0 && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddingNew(true)}
            disabled={disabled || saving}
            sx={{ textTransform: 'none' }}
          >
            Add Mapping
          </Button>
        )}
      </Box>

      {mappings.length === 0 && !addingNew ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          No combo mappings configured for this lab.
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Analysis Combo</TableCell>
                <TableCell>Sage Item</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {mapping.comboName || `Combo #${mapping.analysisComboId}`}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <SageItemAutocomplete
                      value={mapping.sageItemId}
                      onChange={(id) => handleSageItemChange(mapping, id)}
                      size="small"
                      disabled={disabled || saving}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={mapping.isActive}
                      onChange={() => handleToggleActive(mapping)}
                      disabled={disabled || saving}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete mapping">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMapping(mapping)}
                        disabled={disabled || saving}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {addingNew && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Analysis Combo</InputLabel>
            <Select
              value={newComboId}
              onChange={(e) => setNewComboId(e.target.value as number)}
              label="Analysis Combo"
              disabled={saving}
            >
              {availableCombos.map((combo) => (
                <MenuItem key={combo.id} value={combo.id}>
                  {combo.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ minWidth: 200, flex: 1 }}>
            <SageItemAutocomplete
              value={newSageItemId}
              onChange={(id) => setNewSageItemId(id)}
              size="small"
              disabled={saving}
            />
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={handleAddMapping}
            disabled={!newComboId || !newSageItemId || saving}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Add
          </Button>
          <Button
            size="small"
            onClick={() => {
              setAddingNew(false);
              setNewComboId('');
              setNewSageItemId(null);
            }}
            disabled={saving}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LabComboMappingsEditor;
